// "ADD FROM <PARTNER>'S PEOPLE" on the Partners page (owner, 2026-10-15: "the
// partner core will combine users from all projects but only show specific
// people on specific projects").
//
// Adding somebody on one project no longer puts them on the others, so this
// is how a person who already works for the partner on another of your
// projects gets onto THIS one: listed, ticked, added in one click. The list
// is the partner's core (back/utils/partnerRosterSync.partnerCore) minus the
// people already on this row; the server recomputes it on add and runs the
// same edit as "+ Add person" (back/routes/
// participants POST /:pid/core), so the mail is that button's: an employee
// with no account gets one and the welcome email, a supervisor gets today's
// partner digest, and nobody else hears anything.
//
// Its own component so ProjectParticipantsPage only gains a button and a tag
// (other work edits that page at the same time).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Empty, Input, Modal, Select, Table, Tag, message } from "antd";
import { addFromPartnerCore, getPartnerCore } from "api/participants/participantsApi";

// What the person can be added as, given who they are elsewhere and what the
// caller may do. An executive is only ever added as an executive (the
// supervisor list with the flag): as an employee they would become an operator.
export function asOptions(p, canSetSupervisors) {
  const isExecOnly = p.executive && !p.supervisor && !p.person;
  const sup = { value: "supervisor", label: isExecOnly ? "Executive" : "Supervisor", disabled: !canSetSupervisors };
  if (isExecOnly) return [sup];
  return [{ value: "user", label: "Employee" }, sup];
}

export function defaultAs(p, canSetSupervisors) {
  if (p.person) return "user";
  return canSetSupervisors ? "supervisor" : "user";
}

export default function PartnerCorePicker({ projectId, row, onClose, onAdded }) {
  const [state, setState] = useState({ loading: false, people: [], hiddenRemoved: 0, canSetSupervisors: false, error: "" });
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [picked, setPicked] = useState([]);
  const [choice, setChoice] = useState({});   // key -> { as, title }
  const [busy, setBusy] = useState(false);
  const partner = (row && (row.companyName || "this partner")) || "";

  const load = useCallback(async () => {
    if (!row) return;
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const r = await getPartnerCore(projectId, row._id, { includeRemoved });
      setState({ loading: false, people: r.people || [], hiddenRemoved: r.hiddenRemoved || 0, canSetSupervisors: !!r.canSetSupervisors, error: "" });
    } catch (e) {
      setState({ loading: false, people: [], hiddenRemoved: 0, canSetSupervisors: false, error: e.message || "Could not list the partner's people" });
    }
  }, [projectId, row, includeRemoved]);

  useEffect(() => { if (row) load(); }, [row, load]);
  useEffect(() => { if (!row) { setPicked([]); setChoice({}); setIncludeRemoved(false); } }, [row]);

  const pick = (p) => choice[p.key] || { as: defaultAs(p, state.canSetSupervisors), title: p.title || "" };
  const setPick = (p, patch) => setChoice((c) => ({ ...c, [p.key]: { ...pick(p), ...patch } }));
  const byKey = useMemo(() => new Map(state.people.map((p) => [p.key, p])), [state.people]);
  const missingTitle = picked.map((k) => byKey.get(k)).filter((p) => p && pick(p).as === "user" && !String(pick(p).title || "").trim());

  const add = async () => {
    if (missingTitle.length) {
      message.warning(`${missingTitle.map((p) => p.name).join(", ")} ${missingTitle.length === 1 ? "needs" : "need"} a title to be added as an employee`);
      return;
    }
    setBusy(true);
    try {
      const people = picked.map((k) => byKey.get(k)).filter(Boolean)
        .map((p) => ({ key: p.key, as: pick(p).as, ...(pick(p).as === "user" ? { title: String(pick(p).title || "").trim() } : {}) }));
      const r = await addFromPartnerCore(projectId, row._id, people);
      const names = (r.added || []).map((a) => a.name).join(", ");
      message.success(`Added to this project: ${names}`);
      if ((r.skipped || []).length) {
        message.warning(`Not added: ${r.skipped.map((s) => `${s.name || s.key} (${s.why})`).join("; ")}`, 8);
      }
      if (onAdded) onAdded(r);
      onClose();
    } catch (e) {
      message.error(e.message || "Could not add them");
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      title: "Name",
      render: (p) => (
        <span>
          {p.name}
          {p.executive && !p.supervisor ? <Tag color="purple" style={{ marginLeft: 6 }}>Executive</Tag> : null}
          {p.supervisor ? <Tag color="blue" style={{ marginLeft: 6 }}>Supervisor</Tag> : null}
          {p.removedHere ? <Tag color="orange" style={{ marginLeft: 6 }}>taken off this project before</Tag> : null}
        </span>
      ),
    },
    {
      title: "Email",
      render: (p) => (p.email ? p.email : <Tag color="gold">needs email</Tag>),
    },
    {
      title: "On",
      render: (p) => (p.projects || []).map((x) => x.title || x.projectId).join(", "),
    },
    {
      title: "Add as",
      width: 140,
      render: (p) => (
        <Select size="small" style={{ width: 128 }} value={pick(p).as}
          onChange={(v) => setPick(p, { as: v })}
          options={asOptions(p, state.canSetSupervisors)} />
      ),
    },
    {
      title: "Title",
      width: 170,
      render: (p) => (pick(p).as === "user" ? (
        <Input size="small" placeholder="e.g. Foreman" value={pick(p).title}
          onChange={(e) => setPick(p, { title: e.target.value })} />
      ) : <span style={{ color: "#888" }}>-</span>),
    },
  ];

  return (
    <Modal
      open={!!row}
      width={860}
      title={`Add from ${partner}'s people`}
      okText={picked.length ? `Add ${picked.length}` : "Add"}
      okButtonProps={{ disabled: !picked.length }}
      confirmLoading={busy}
      onCancel={onClose}
      onOk={add}
      destroyOnClose
    >
      <div style={{ color: "#666", fontSize: 12, marginBottom: 8 }}>
        {partner}&apos;s people on your other projects who are not on this one. Each person ticked is added to this
        project only. An employee without an account gets one and the usual welcome email; a supervisor gets today&apos;s
        partner digest - the same as &quot;+ Add person&quot;.
        {!state.canSetSupervisors ? " Only the project owner or an administrator can add somebody as a supervisor." : ""}
      </div>
      {state.error ? <Alert type="error" showIcon message={state.error} style={{ marginBottom: 8 }} /> : null}
      {state.hiddenRemoved > 0 && !includeRemoved ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message={`${state.hiddenRemoved} ${state.hiddenRemoved === 1 ? "person was" : "people were"} taken off this project before and ${state.hiddenRemoved === 1 ? "is" : "are"} not listed.`}
          action={<Button size="small" onClick={() => setIncludeRemoved(true)}>Show them</Button>}
        />
      ) : null}
      <Table
        size="small"
        rowKey="key"
        loading={state.loading}
        dataSource={state.people}
        columns={columns}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        rowSelection={{
          selectedRowKeys: picked,
          onChange: (keys) => setPicked(keys),
          getCheckboxProps: (p) => {
            const opts = asOptions(p, state.canSetSupervisors);
            return { disabled: opts.every((o) => o.disabled), name: p.name };
          },
        }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`Nobody from ${partner} on your other projects who is not already here`} /> }}
      />
      {missingTitle.length ? (
        <div style={{ color: "#B45309", fontSize: 12, marginTop: 6 }}>
          Type a title (their role on the job) for {missingTitle.map((p) => p.name).join(", ")} to add them as
          {missingTitle.length === 1 ? " an employee" : " employees"}.
        </div>
      ) : null}
    </Modal>
  );
}
